import { toDisplayOddsDecimal } from './oddsFormat.js';

const UNLIMITED_BETTING_CAP = Number.MAX_SAFE_INTEGER;
const DEFAULT_BETTING_LIMITS = {
  global_min_bet: 5000,
  global_max_bet: 10000000,
  single_max_bet: 10000000,
  combo_max_bet: 5000000,
  global_max_payout: 50000000,
  min_folder_count: 1,
  max_folder_count: 10,
  max_total_odds: UNLIMITED_BETTING_CAP,
};

const toLooseNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '').trim();
    if (!cleaned) return NaN;
    return Number(cleaned);
  }
  return Number(value);
};

const normalizePositiveLimit = (value, fallback) => {
  const num = toLooseNumber(value);
  return Number.isFinite(num) && num > 0 ? num : fallback;
};

const resolveUpperBoundLimit = (value, fallback, isUnlimited = false) => {
  if (isUnlimited) return UNLIMITED_BETTING_CAP;
  return normalizePositiveLimit(value, fallback);
};

const isUnlimitedValue = (value) => {
  if (value == null || value === '') return true;
  const num = toLooseNumber(value);
  return Number.isFinite(num) && num <= 0;
};

const unwrapResponseData = (source = {}) => {
  if (!source || typeof source !== 'object') return source;
  if (source.data && typeof source.data === 'object') return source.data;
  return source;
};

const resolveSportsnewLimitSource = (source = {}) => {
  const unwrapped = unwrapResponseData(source);
  if (!unwrapped || typeof unwrapped !== 'object') return null;

  const bettingLimits = unwrapped.bettingLimits || unwrapped.betting_limits;
  const comboRules = unwrapped.comboRules || unwrapped.combo_rules;
  const comboOdds = unwrapped.comboOdds || unwrapped.combo_odds;
  if (!bettingLimits && !comboRules && !comboOdds) return null;

  const maxStake = bettingLimits?.max_stake;
  const maxSingleStake = bettingLimits?.max_single_stake ?? maxStake;
  const maxComboStake = bettingLimits?.max_combo_stake ?? maxStake;
  const maxPayout = bettingLimits?.max_payout;
  const maxTotalOdds = comboOdds?.max_total_odds;

  return {
    limits: {
      global_min_bet: bettingLimits?.min_stake,
      global_max_bet: maxStake,
      single_max_bet: maxSingleStake,
      combo_max_bet: maxComboStake,
      global_max_payout: maxPayout,
      min_folder_count: comboRules?.min_legs,
      max_folder_count: comboRules?.max_legs,
      max_total_odds: maxTotalOdds,
    },
    unlimited: {
      global_max_bet: isUnlimitedValue(maxStake),
      single_max_bet: isUnlimitedValue(maxSingleStake),
      combo_max_bet: isUnlimitedValue(maxComboStake),
      global_max_payout: isUnlimitedValue(maxPayout),
      effective_max_bet: isUnlimitedValue(maxStake),
      max_total_odds: isUnlimitedValue(maxTotalOdds),
    },
    effective_max_bet: maxStake,
  };
};

const resolveLimitSource = (source = {}) => {
  const sportsnew = resolveSportsnewLimitSource(source);
  if (sportsnew) return sportsnew;

  if (source && typeof source === 'object' && source.limits && typeof source.limits === 'object') {
    return {
      limits: source.limits,
      unlimited: source.unlimited || {},
      effective_max_bet: source.effective_max_bet,
    };
  }

  return {
    limits: source || {},
    unlimited: {},
    effective_max_bet: undefined,
  };
};

const roundOdds = (value) => toDisplayOddsDecimal(value) ?? 0;

const pickResolvedBalance = (source, seen) => {
  if (source == null) return null;

  const direct = toLooseNumber(source);
  if (Number.isFinite(direct)) return Math.max(0, direct);

  if (typeof source !== 'object') return null;
  if (seen.has(source)) return null;
  seen.add(source);

  const candidates = [
    source.wallet?.available_balance,
    source.wallet?.balance,
    source.available_balance,
    source.balance,
    source.cash,
    source.money,
    source.user?.wallet?.available_balance,
    source.user?.wallet?.balance,
    source.user?.available_balance,
    source.user?.balance,
    source.UPDATE,
    source.data,
  ];

  for (const candidate of candidates) {
    const resolved = pickResolvedBalance(candidate, seen);
    if (resolved != null) return resolved;
  }
  return null;
};

export const resolveAvailableBalance = (...sources) => {
  const seen = new WeakSet();
  for (const source of sources) {
    const resolved = pickResolvedBalance(source, seen);
    if (resolved != null) return resolved;
  }
  return 0;
};

export const normalizeBettingLimits = (source = {}) => {
  const {
    limits: raw = {},
    unlimited = {},
    effective_max_bet: effectiveMaxBet,
  } = resolveLimitSource(source);

  return {
    global_min_bet: normalizePositiveLimit(raw.global_min_bet, DEFAULT_BETTING_LIMITS.global_min_bet),
    global_max_bet: resolveUpperBoundLimit(
      raw.global_max_bet,
      DEFAULT_BETTING_LIMITS.global_max_bet,
      unlimited.global_max_bet === true
    ),
    single_max_bet: resolveUpperBoundLimit(
      raw.single_max_bet,
      DEFAULT_BETTING_LIMITS.single_max_bet,
      unlimited.single_max_bet === true
    ),
    combo_max_bet: resolveUpperBoundLimit(
      raw.combo_max_bet,
      DEFAULT_BETTING_LIMITS.combo_max_bet,
      unlimited.combo_max_bet === true
    ),
    global_max_payout: resolveUpperBoundLimit(
      raw.global_max_payout,
      DEFAULT_BETTING_LIMITS.global_max_payout,
      unlimited.global_max_payout === true
    ),
    effective_max_bet: resolveUpperBoundLimit(
      effectiveMaxBet,
      DEFAULT_BETTING_LIMITS.global_max_bet,
      unlimited.effective_max_bet === true
    ),
    min_folder_count: normalizePositiveLimit(raw.min_folder_count, DEFAULT_BETTING_LIMITS.min_folder_count),
    max_folder_count: resolveUpperBoundLimit(
      raw.max_folder_count,
      DEFAULT_BETTING_LIMITS.max_folder_count,
      unlimited.max_folder_count === true
    ),
    max_total_odds: resolveUpperBoundLimit(
      raw.max_total_odds,
      DEFAULT_BETTING_LIMITS.max_total_odds,
      unlimited.max_total_odds === true
    ),
    unlimited: {
      global_max_bet: unlimited.global_max_bet === true,
      single_max_bet: unlimited.single_max_bet === true,
      combo_max_bet: unlimited.combo_max_bet === true,
      global_max_payout: unlimited.global_max_payout === true,
      effective_max_bet: unlimited.effective_max_bet === true,
      max_folder_count: unlimited.max_folder_count === true,
      max_total_odds: unlimited.max_total_odds === true,
    },
  };
};

export const getDisplayedFinalOdds = ({ betType, selections, totalOdds, bonusMultiplier }) => {
  if (betType === 'SINGLE') {
    return roundOdds(selections?.[0]?.odds || 0);
  }
  const baseOdds = roundOdds(totalOdds || 0);
  const bonus = Number(bonusMultiplier || 1);
  return roundOdds(baseOdds * bonus);
};

export const computeDisplayedPotentialWin = ({
  stake,
  displayedFinalOdds,
  bettingLimits,
}) => {
  const limits = normalizeBettingLimits(bettingLimits);
  const safeStake = Math.max(0, Math.floor(toLooseNumber(stake) || 0));
  const safeOdds = roundOdds(displayedFinalOdds || 0);
  if (safeStake <= 0 || safeOdds <= 0) return 0;
  return Math.floor(Math.min(safeStake * safeOdds, limits.global_max_payout));
};

export const computeBetCartMaxStake = ({
  balance,
  betType,
  bettingLimits,
  displayedFinalOdds,
}) => {
  const limits = normalizeBettingLimits(bettingLimits);
  const safeBalanceRaw = toLooseNumber(balance);
  const safeBalance = Math.max(0, Math.floor(Number.isFinite(safeBalanceRaw) ? safeBalanceRaw : 0));
  const fallbackBetTypeCap = betType === 'COMBO'
    ? Math.min(limits.global_max_bet, limits.combo_max_bet)
    : Math.min(limits.global_max_bet, limits.single_max_bet);
  const betTypeCap = Math.min(fallbackBetTypeCap, limits.effective_max_bet);
  const payoutCapStake = displayedFinalOdds > 0
    ? Math.floor(limits.global_max_payout / displayedFinalOdds)
    : betTypeCap;
  const maxStake = Math.max(0, Math.floor(Math.min(safeBalance, betTypeCap, payoutCapStake)));

  return {
    limits,
    betTypeCap,
    payoutCapStake: Number.isFinite(payoutCapStake) ? Math.max(0, payoutCapStake) : betTypeCap,
    maxStake,
    canBetAtLeastMin: maxStake >= limits.global_min_bet,
  };
};

export const computeRawPotentialWin = ({ stake, displayedFinalOdds }) => {
  const safeStake = Math.max(0, Math.floor(toLooseNumber(stake) || 0));
  const safeOdds = roundOdds(displayedFinalOdds || 0);
  if (safeStake <= 0 || safeOdds <= 0) return 0;
  return Math.floor(safeStake * safeOdds);
};

export const validateBetCartLimits = ({
  selections,
  stake,
  bettingLimits,
  displayedFinalOdds,
  maxStakeInfo,
}) => {
  const selectionCount = Array.isArray(selections) ? selections.length : 0;
  const limits = maxStakeInfo?.limits || normalizeBettingLimits(bettingLimits);
  const stakeValue = Math.floor(toLooseNumber(stake) || 0);
  const finalOdds = roundOdds(displayedFinalOdds || 0);

  if (selectionCount > 0 && selectionCount < limits.min_folder_count) {
    return { ok: false, message: `최소 ${limits.min_folder_count}폴더부터 베팅할 수 있습니다` };
  }

  if (!limits.unlimited.max_folder_count && selectionCount > limits.max_folder_count) {
    return { ok: false, message: `최대 ${limits.max_folder_count}폴더까지 선택할 수 있습니다` };
  }

  if (!limits.unlimited.max_total_odds && finalOdds > limits.max_total_odds) {
    return { ok: false, message: `최대 배당 ${limits.max_total_odds}배를 초과할 수 없습니다` };
  }

  if (stakeValue < limits.global_min_bet) {
    return { ok: false, message: `최소 베팅액은 ${Number(limits.global_min_bet).toLocaleString('ko-KR')}원입니다` };
  }

  if (maxStakeInfo && stakeValue > maxStakeInfo.maxStake) {
    if (stakeValue > maxStakeInfo.betTypeCap) {
      return { ok: false, message: `최대 베팅 가능 금액은 ${Number(maxStakeInfo.betTypeCap).toLocaleString('ko-KR')}원입니다` };
    }
    if (stakeValue > maxStakeInfo.payoutCapStake) {
      return { ok: false, message: `최대 당첨 가능 금액은 ${Number(limits.global_max_payout).toLocaleString('ko-KR')}원입니다` };
    }
    return { ok: false, message: '보유금액을 초과했습니다' };
  }

  const rawPotentialWin = computeRawPotentialWin({ stake: stakeValue, displayedFinalOdds: finalOdds });
  if (!limits.unlimited.global_max_payout && rawPotentialWin > limits.global_max_payout) {
    return { ok: false, message: `최대 당첨 가능 금액은 ${Number(limits.global_max_payout).toLocaleString('ko-KR')}원입니다` };
  }

  return { ok: true };
};
